import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";
import SearchBar from "@/components/feature/SearchBar";

const navLinks = [
  { key: "nav.home", path: "/" },
  { key: "nav.live", path: "/live" },
  { key: "nav.news", path: "/news" },
  { key: "nav.programs", path: "/programs" },
  { key: "nav.videos", path: "/videos" },
  { key: "nav.schedule", path: "/schedule" },
  { key: "nav.entertainment", path: "/entertainment" },
  { key: "nav.sports", path: "/sports" },
  { key: "nav.photoStudio", path: "/photo-studio" },
  { key: "nav.production", path: "/production" },
  { key: "nav.gallery", path: "/gallery" },
  { key: "nav.about", path: "/about" },
  { key: "nav.contact", path: "/contact" },
  { key: "nav.adverts", path: "/adverts" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="ltv-container">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img
              src="https://storage.helloreaddy.io/project_files/f3582b29-89ce-4104-acc4-d71527c07beb/9c1ed9ab-5b65-494b-810b-15bf34b3d622_compressed_IMG-20260825-WA0008.webp"
              alt="L+TV TÉLÉVISION"
              className="h-10 md:h-12 w-auto object-contain"
            />
            <div className="hidden sm:block">
              <span className={`text-xs font-medium block transition-colors ${scrolled ? "text-foreground-700" : "text-white/80"}`}>
                {t("nav.edea")}
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.slice(0, 6).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-2.5 py-2 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                  isActive(link.path)
                    ? scrolled
                      ? "text-accent-500 bg-accent-50"
                      : "text-accent-400 bg-white/10"
                    : scrolled
                      ? "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
                      : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {t(link.key)}
              </Link>
            ))}
            <div className="relative group">
              <button
                className={`px-2.5 py-2 text-xs font-medium rounded-md transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer ${
                  scrolled
                    ? "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {t("nav.more")}
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-background-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
                {navLinks.slice(6).map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-2 text-sm transition-colors ${
                      isActive(link.path)
                        ? "text-accent-500 bg-accent-50"
                        : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
                    }`}
                  >
                    {t(link.key)}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* Right side: Language, Search, Watch Live + Hamburger */}
          <div className="flex items-center gap-2 md:gap-3">
            <SearchBar scrolled={scrolled} />
            <LanguageSwitcher scrolled={scrolled} />

            <Link
              to="/live"
              className="hidden md:flex items-center gap-1.5 bg-accent-500 hover:bg-accent-600 text-white text-xs font-bold px-4 py-2 rounded-md transition-all whitespace-nowrap"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              {t("nav.watchLive")}
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 rounded-md transition-colors ${
                scrolled ? "text-foreground-800 hover:bg-background-200" : "text-white bg-black/30 hover:bg-black/40"
              }`}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden bg-white border-b border-background-200 overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-[85vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="ltv-container py-3 overflow-y-auto max-h-[80vh]">
          <Link
            to="/live"
            className="flex md:hidden items-center justify-center gap-1.5 bg-accent-500 hover:bg-accent-600 text-white text-sm font-bold px-4 py-2.5 rounded-md transition-all mb-3"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            {t("nav.watchLive")}
          </Link>

          {/* Mobile language row */}
          <div className="flex items-center justify-between px-1 py-2 mb-1 border-b border-background-100">
            <span className="text-xs text-foreground-500">Language / Langue</span>
            <LanguageSwitcher scrolled />
          </div>

          <div className="space-y-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                  isActive(link.path)
                    ? "text-accent-500 bg-accent-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
                }`}
              >
                {t(link.key)}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}